import { useState } from 'react'
import { useUsuarios } from '../../../api/queries/usuarios'
import { useSessao } from '../../../auth/contexto'
import { Botao } from '../../../components/Botao'
import { AvisoDeErro, Esqueleto, EstadoVazio } from '../../../components/Estados'
import { IconeBusca, IconeNovo, IconeUsuarios } from '../../../components/Icones'
import { CampoDeTexto } from '../../../components/campos'
import { TEXTOS } from '../../../lib/glossario'
import type { UsuarioAdministrado } from '../../../types/usuario'
import { DialogoDeNovaConta } from './DialogoDeNovaConta'
import { DialogoDePapeis } from './DialogoDePapeis'
import { DialogoDeSenha } from './DialogoDeSenha'
import { TabelaDeContas } from './TabelaDeContas'

/**
 * Quem tem acesso ao sistema e o que cada um pode fazer.
 *
 * Até esta tela existir, dar papel a alguém era um `INSERT` no psql: o Entra ID está implementado
 * e sem credenciais neste ambiente, e o provisionamento automático concede apenas "somente
 * consulta". Montar o elenco de um teste com usuários reais dependia do banco, uma pessoa por vez.
 *
 * @returns a tela de administração de contas
 */
export function PaginaAdminUsuarios() {
  const [busca, setBusca] = useState('')
  const [criando, setCriando] = useState(false)
  const [redefinindo, setRedefinindo] = useState<UsuarioAdministrado | null>(null)
  const [mudandoPapeis, setMudandoPapeis] = useState<UsuarioAdministrado | null>(null)
  const { usuario } = useSessao()
  const { data: contas, isPending, isError, error } = useUsuarios(busca)

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="titulo-da-pagina">{TEXTOS.usuarios.titulo}</h1>
          <p className="mt-1 max-w-prose text-sm text-slate-600">{TEXTOS.usuarios.ajuda}</p>
        </div>
        <Botao aparencia="primario" onClick={() => setCriando(true)}>
          <IconeNovo className="h-4 w-4" />
          Cadastrar conta
        </Botao>
      </header>

      <div className="cartao max-w-md p-4">
        <CampoDeTexto
          rotulo="Buscar"
          ajuda="Por trecho do nome ou do e-mail."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
      </div>

      {isPending && (
        <div className="cartao px-4 py-5">
          <Esqueleto linhas={6} />
        </div>
      )}
      {isError && <AvisoDeErro erro={error} titulo="Não foi possível carregar as contas" />}

      {contas && contas.length === 0 && (
        <EstadoVazio
          icone={busca ? <IconeBusca className="h-9 w-9" /> : <IconeUsuarios className="h-9 w-9" />}
          titulo={TEXTOS.usuarios.listaVazia.titulo}
          ajuda={TEXTOS.usuarios.listaVazia.ajuda}
          acao={busca ? <Botao onClick={() => setBusca('')}>{TEXTOS.limparFiltros}</Botao> : undefined}
        />
      )}

      {contas && contas.length > 0 && (
        <TabelaDeContas
          contas={contas}
          usuarioLogadoId={usuario?.id}
          aoRedefinirSenha={(conta) => setRedefinindo(conta)}
          aoAlterarPapeis={(conta) => setMudandoPapeis(conta)}
        />
      )}

      {criando && <DialogoDeNovaConta aoFechar={() => setCriando(false)} />}
      {redefinindo && <DialogoDeSenha conta={redefinindo} aoFechar={() => setRedefinindo(null)} />}
      {mudandoPapeis && <DialogoDePapeis conta={mudandoPapeis} aoFechar={() => setMudandoPapeis(null)} />}
    </div>
  )
}
